import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Button, Col, Input, Row, Divider, Select } from "antd";
import axios from "axios";
import ReactQuill from "react-quill";
import { CloseCircleOutlined, LeftOutlined } from "@ant-design/icons";
import { createSearchParams, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import GenerateContentContainer from "./components/GenerateContentContainer";
import OpenAiTaskModal from "./components/OpenAiTaskModal";
import Loading from "../components/Loading";

import { openAiActions } from "./duck/openAiReducer";
import { useQuery } from "../utils/customHooks";

import appUrl from "../config/appUrl";
import {
  arrayToMap,
  hasApiCallError,
  showNotification,
} from "../utils/commonFunctions";
import routes from "../utils/routes";
import constants from "../utils/constants";

import "./OpenAi.scss";
import AppUrl from "../config/appUrl";

const { AUTO_GENERATE_CONTENT_TYPES, OPEN_AI_TASK_FIELDS, TOP_LANGUAGES } =
  constants;

const Translator = () => {
  const prevProps = useRef();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const query = useQuery();

  const openAi = useSelector(({ openAi }) => openAi);
  const mTask = useMemo(() => {
    const taskId = query.get("task_id");
    return openAi.map[taskId] || {};
  }, [query, openAi.map]);

  const [state, setState] = useState({
    selectedLanguages: [],
  });
  const [error, setError] = useState({});
  const [responseAnswers, setResponseAnswers] = useState([]);
  const [loading, setLoading] = useState(false);

  // Ai model start
  const [aiModel, setAiModel] = useState([]);
  const [modelValue, setModelValue] = useState("gpt-4");

  useEffect(() => {
    axios.get(`${AppUrl.OPENAI_MODEL_LIST}`).then((response) => {
      setAiModel(response?.data?.data);
    });
  }, []);

  const handleModelSelect = (value) => {
    setModelValue(value);
    //localStorage.setItem("aiModel", value);
  };
  // Ai model end

  useEffect(() => {
    if (
      prevProps.current?.loading &&
      !openAi?.loading &&
      state.isTaskCreating
    ) {
      const searchParams = {
        task_id: openAi?.newCreatedDataId,
      };

      const navigateTo = {
        search: `?${createSearchParams(searchParams)}`,
      };
      navigate(navigateTo, { replace: true });
      setState((prevState) => ({ ...prevState, isTaskCreating: false }));
    } else if (
      prevProps.current?.loading &&
      !mTask?.loading &&
      state.isTaskSaving
    ) {
      if (!mTask?.error) {
        const navigateTo = routes.OPEN_AI_DASHBOARD;
        navigate(navigateTo, { replace: true });
      }
    }

    return () => {
      prevProps.current = {
        loading: openAi?.loading || mTask?.loading,
        isTaskCreating: state.isTaskCreating,
        isTaskSaving: state.isTaskSaving,
      };
    };
  }, [
    openAi?.newCreatedDataId,
    openAi?.loading,
    state?.isTaskCreating,
    state?.isTaskSaving,
    mTask?.loading,
    mTask?.error,
    dispatch,
    navigate,
  ]);

  useEffect(() => {
    const taskId = query.get("task_id");
    if (!taskId) {
      setState((prevState) => ({ ...prevState, isTaskCreating: true }));
      const payload = {
        fields: [...OPEN_AI_TASK_FIELDS],
      };
      payload.fields.forEach((field) => {
        if (field._id === "content_type") {
          field.value = AUTO_GENERATE_CONTENT_TYPES.TRANSLATOR.name;
        }
      });
      dispatch(openAiActions.onCreateOneRequest(payload));
    } else {
      dispatch(openAiActions.onGetOneRequest({ _id: taskId }));
    }
  }, [query, dispatch]);

  const hasError = useCallback(() => {
    let { message, selectedLanguages } = state;
    message = message?.trim?.();

    const error = {};

    if (!message) {
      error.message = "Please enter message.";
    }
    if (!selectedLanguages?.length) {
      error.selectedLanguages =
        "Please select atleast one language for translation";
    }

    setError(error);
    return Object.keys(error).length > 0;
  }, [state]);

  const translateApi = useCallback(() => {
    if (!hasError()) {
      setLoading(true);
      let { message, selectedLanguages } = state;
      message = message?.trim?.();

      let text = `Convert "${message}" into ${selectedLanguages?.join(
        ", "
      )} languages`;

      const payload = { text, model: modelValue };
      if (mTask?._id) payload.task_id = mTask?._id;

      axios({
        method: "POST",
        url: `${appUrl.OPENAI}/default`,
        data: payload,
      })
        .then((response) => {
          const resData = response.data;

          if (!hasApiCallError(resData?.meta)) {
            const choiceText = resData?.data?.content;
            let answers = choiceText?.split?.("\n") || [];
            if (Array.isArray(answers) && answers.length > 0) {
              answers = answers.filter((answer) => answer?.trim?.());
              answers = answers.map((answer, i) => {
                answer =
                  answer
                    ?.match?.(/(?:"[^"]*"|^[^"]*$)/)?.[0]
                    ?.replace?.(/"/g, "") || answer;

                if (answer.startsWith(`${i + 1}.`)) {
                  answer = answer?.replace(`${i + 1}.`, "");
                }

                return answer?.trim?.();
              });

              answers = answers.filter((answer) => answer?.trim?.());
              setResponseAnswers(answers);
            }

            const mDraft = answers?.reduce((acc, item) => {
              let finalDraft = acc;
              if (finalDraft) finalDraft = `${finalDraft}<br>`;
              finalDraft = `${finalDraft}<p>${item}</p>`;

              return finalDraft;
            }, "");

            setState((prevState) => ({
              ...prevState,
              finalDraft: mDraft,
            }));
          }

          setLoading(false);
        })
        .catch((err) => {
          console.log("Error", err);
          showNotification("error", "Something went wrong");
          setLoading(false);
        });
    }
  }, [hasError, state, mTask?._id, modelValue]);

  const handleChange = useCallback(
    (name) => (e) => {
      let value = e?.target?.value ?? e;

      setState((prevState) => ({ ...prevState, [name]: value }));
      setError({});
    },
    []
  );

  const onGoBack = useCallback(() => navigate(-1), [navigate]);

  const _dropdownRender = useCallback(
    (menu) => (
      <Col className="fw" span={24}>
        {menu}
        {state?.selectedLanguages?.length >= 3 && (
          <>
            <Divider style={{ margin: "4px 0" }} />
            <Col className="p5" span={24}>
              <Col>
                <Row className="error mb5">Maximum limit reached</Row>
              </Col>
            </Col>
          </>
        )}
      </Col>
    ),
    [state?.selectedLanguages?.length]
  );

  const onSubmit = useCallback(() => {
    setState((prevState) => ({ ...prevState, finalDraft: "" }));
    translateApi();
  }, [translateApi]);

  const handleShowOpenAiTaskModal = useCallback((show) => {
    show = typeof show === "boolean" && show;
    setState((prevState) => ({
      ...prevState,
      showOpenAiTaskModal: show,
    }));
  }, []);

  const onSave = useCallback(
    (fields = []) => {
      const { _id } = mTask;

      const payload = {
        _id: _id,
        name: "",
        fields: [],
        draft: false,
      };

      const fieldMap = Object.assign(
        {},
        arrayToMap(mTask?.fields),
        arrayToMap(fields)
      );
      payload.fields = mTask?.fields?.map((field) => {
        const newField = Object.assign({}, field, fieldMap[field?._id]);
        if (newField?._id === "content_type") {
          newField.value = AUTO_GENERATE_CONTENT_TYPES.TRANSLATOR.name;
        } else if (newField?._id === "task_name") {
          payload.name = newField.value;
        } else if (newField?._id === "content") {
          newField.value = state.finalDraft;
        }

        return newField;
      });

      setState((prevState) => ({
        ...prevState,
        showOpenAiTaskModal: false,
        isTaskSaving: true,
      }));
      dispatch(openAiActions.onUpdateOneRequest(payload));
    },
    [mTask, state, dispatch]
  );

  if (
    (!openAi.newCreatedDataId && openAi.loading) ||
    state.isTaskCreating ||
    (!mTask?._id && mTask?.loading)
  ) {
    return <Loading />;
  }

  return (
    <>
      <GenerateContentContainer containerClassName="set-container">
        <Row className="content-container">
          <Row className="fw" justify="space-between">
            <Col
              className="--left-container"
              xs={24}
              sm={24}
              md={24}
              lg={11}
              xl={11}
              xxl={11}
            >
              <Col className="title-container mb15">
                <Row className="d-flex-open-ai" align="middle">
                  <div className="back-icon mr-3">
                    <LeftOutlined className="text-2xl" onClick={onGoBack} />
                  </div>
                  <span>ChatGPT Translator</span>
                  {/*Ai model html code start*/}
                  <Row align="middle" className="ml-auto">
                    <Col style={{ fontSize: "14px", paddingRight: "10px" }}>
                      Switch model :
                    </Col>
                    <Col>
                      <Select
                        className="new_select fw"
                        style={{ minWidth: 160 }}
                        placeholder="Select Model"
                        // suffixIcon={<CaretDownOutlined style={{ color: "#fff" }} />}
                        clearIcon={
                          <CloseCircleOutlined
                            style={{
                              color: "#fff",
                              background: "#000",
                              fontSize: 13,
                            }}
                          />
                        }
                        size="middle"
                        dropdownMatchSelectWidth={false}
                        value={modelValue}
                        onChange={handleModelSelect}
                      >
                        {aiModel?.map?.((item, i) => (
                          <Select.Option key={item?.id} value={item?.id}>
                            {item?.id}
                          </Select.Option>
                        ))}
                      </Select>
                    </Col>
                  </Row>
                  {/*Ai model html code end*/}
                </Row>
              </Col>

              <Col className="mb15">
                <Col className="mb5">
                  <label className="label">Select language</label>
                  <sup style={{ color: "red" }}>*</sup>
                </Col>

                <Select
                  className="custom-select fw"
                  placeholder="Select maximum 3 langauges"
                  mode="multiple"
                  value={state.selectedLanguages}
                  onChange={handleChange("selectedLanguages")}
                  dropdownRender={_dropdownRender}
                  size="large"
                  disabled={loading}
                  showSearch
                  allowClear
                >
                  {TOP_LANGUAGES.map((lang, i) => (
                    <Select.Option
                      disabled={
                        !state?.selectedLanguages?.includes(lang) &&
                        state?.selectedLanguages?.length === 3
                      }
                      key={i}
                      title={lang}
                      value={lang}
                    />
                  ))}
                </Select>

                <Row className="error mt5">{error.selectedLanguages}</Row>
              </Col>
              <Col className="mb15">
                <Col className="mb5">
                  <label className="label">
                    What do you want to translate?
                  </label>
                  <sup style={{ color: "red" }}>*</sup>
                </Col>

                <Input.TextArea
                  className="input input-area"
                  placeholder="Enter text"
                  value={state.message}
                  autoSize={{ minRows: 8, maxRows: 10 }}
                  onChange={handleChange("message")}
                  size="large"
                  disabled={loading}
                />

                <Row className="error mt5">{error.message}</Row>
              </Col>

              <Col className="mt20">
                <Button
                  className="fw"
                  type="primary"
                  loading={loading}
                  onClick={onSubmit}
                  size="large"
                >
                  Translate
                </Button>
              </Col>
            </Col>

            <Col xs={0} sm={0} md={0} lg={1} xl={1} xxl={1} align="center">
              <Divider type="vertical" />
            </Col>

            <Col xs={24} sm={24} md={24} lg={0} xl={0} xxl={0} align="center">
              <Divider />
            </Col>

            <Col
              className="--right-container"
              xs={24}
              sm={24}
              md={24}
              lg={24}
              xl={11}
              xxl={11}
            >
              <Col className="sticky top-0">
                <Col className="h-96 mb-16">
                  <ReactQuill
                    className="input-text-editor h-full"
                    value={state.finalDraft}
                    onChange={handleChange("finalDraft")}
                    theme="snow"
                  />
                </Col>

                <Col xs={12} sm={12} md={12} lg={12} xl={12} xxl={12}>
                  <Row
                    className="w-full"
                    align="middle"
                    justify="space-between"
                  >
                    {mTask?._id && (
                      <Col xs={24} sm={24} md={24} lg={11} xl={11} xxl={11}>
                        <Button
                          className="w-full"
                          type="success"
                          size="large"
                          loading={mTask?.loading}
                          onClick={() => handleShowOpenAiTaskModal(true)}
                        >
                          {mTask?.loading ? "Saving" : "Save"}
                        </Button>
                      </Col>
                    )}
                  </Row>
                </Col>
              </Col>
            </Col>
          </Row>
        </Row>
      </GenerateContentContainer>

      <OpenAiTaskModal
        visible={state.showOpenAiTaskModal}
        taskId={mTask?._id}
        fields={mTask?.fields?.filter((field) => field?._id === "task_name")}
        onSubmit={onSave}
        handleModal={handleShowOpenAiTaskModal}
      />
    </>
  );
};

export default Translator;
